import { makeStyles } from '@material-ui/styles'
import { RootState } from 'app/store'
import ServerSidePagination from 'core/components/server-side-pagination/ServerSidePagination'
import DataKeys from 'k8s/DataKeys'
import { useSelector } from 'react-redux'

interface VMServerSidePaginationProps {
  dataKey: DataKeys
  currentPage: number
  pageSize: number
  onPageChange: (newPage: number, marker?: string) => void
  onPageSizeChange: (newPageSize: number) => void
  totalCount?: number
  hasNextPage?: boolean
  nextMarker?: string | null
}

/**
 * @summary - This is a wrapper component over ServerSidePagination for showing the pagination table footer
 * This wrapper calculates the total count and next marker from the session state
 * @param DataKeys
 * @param
 * @returns
 */
export default function VMServerSidePagination({
  dataKey,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  totalCount,
  hasNextPage,
  nextMarker,
}: VMServerSidePaginationProps) {
  // Get pagination metadata from session state
  const paginationMeta = useSelector((state: RootState) => {
    const session = state.session

    // Map dataKey to corresponding session pagination field
    if (dataKey === DataKeys.AllOpenstackVirtualMachines) {
      return session?.allVmsPagination as
        | {
            currentMarker?: string | null
            nextMarker?: string | null
            hasMore?: boolean
            pageSize?: number
            totalCount?: number
          }
        | undefined
    } else if (dataKey === DataKeys.SelfOpenstackVirtualMachines) {
      return session?.selfVmsPagination as
        | {
            currentMarker?: string | null
            nextMarker?: string | null
            hasMore?: boolean
            pageSize?: number
            totalCount?: number
          }
        | undefined
    } else {
      return session?.vmsPagination as
        | {
            currentMarker?: string | null
            nextMarker?: string | null
            hasMore?: boolean
            pageSize?: number
            totalCount?: number
          }
        | undefined
    }
  })

  // Get current loaded data count
  const loadedCount = useSelector((state: RootState) => {
    const cachedData = state.cache?.cachedData?.[dataKey]?.ids
    return Array.isArray(cachedData) ? cachedData.length : 0
  })

  // Use total count with priority:
  // 1. Session pagination totalCount (from search)
  // 2. totalCount prop (from lite API)
  // 3. loadedCount (fallback)
  const displayTotal =
    paginationMeta?.totalCount != null
      ? paginationMeta.totalCount
      : totalCount != null
      ? totalCount
      : loadedCount

  return (
    <ServerSidePagination
      currentPage={currentPage}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      totalCount={displayTotal}
      hasNextPage={hasNextPage}
      nextMarker={nextMarker || paginationMeta?.nextMarker}
    />
  )
}
